import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Shield, LogOut, Users, Package, DollarSign, TrendingUp,
  UserPlus, Edit, Trash2, Building2, BarChart3, Settings,
  CheckCircle, XCircle, Eye, Facebook, ShieldCheck, Home,
  Ticket, HelpCircle, KeyRound, MessageSquarePlus, Wallet,
  Calculator
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import FacebookGroupsAdmin from "../components/FacebookGroupsAdmin";
import TwoFactorSetup from "../components/TwoFactorSetup";
import UserManagement from "../components/UserManagement";
import SupportTicketManager from "../components/SupportTicketManager";
import TransactionManagement from "../components/TransactionManagement";
import TransactionDashboard from "../components/TransactionDashboard";
import PlatformDocumentation from "../components/PlatformDocumentation";
import AdminPermissionsManager from "../components/AdminPermissionsManager";
import PopupManager from "../components/PopupManager";
import RatingsManager from "../components/RatingsManager";
import SMSNotificationManager from "../components/SMSNotificationManager";
import NotificationTemplateManager from "../components/NotificationTemplateManager";
import FAQManager from "../components/FAQManager";
import PlatformManagement from "../components/PlatformManagement";
import TestimonialManagement from "../components/TestimonialManagement";
import FinancialDashboard from "../components/FinancialDashboard";
import TransferAlerts from "../components/TransferAlerts";
import FraudNewsAdmin from "../components/FraudNewsAdmin";
import PartnerManagement from "../components/PartnerManagement";
import PromoCodeManagement from "../components/PromoCodeManagement";
import AuditLogViewer from "../components/AuditLogViewer";
import { Bell, Star, MessageSquare, FileText, BookOpen, AlertTriangle, Newspaper, Handshake, Gift } from "lucide-react";

// Notification Badge Component
const NotificationBadge = ({ count, variant = "default" }) => {
  if (!count || count <= 0) return null;
  
  const bgColor = variant === "warning" 
    ? "bg-orange-500" 
    : variant === "success" 
    ? "bg-green-500" 
    : "bg-red-500";
  
  return (
    <span 
      className={`absolute -top-0.5 -right-0.5 ${bgColor} text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-0.5 shadow-sm`}
      data-testid="notification-badge"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
};

// Mobile Navigation Button
const MobileNavButton = ({ icon: Icon, label, isActive, onClick, badge, badgeVariant }) => (
  <button
    onClick={onClick}
    className={`relative flex flex-col items-center justify-center p-2 rounded-lg transition-all ${
      isActive 
        ? 'bg-brand-600 text-white shadow-md' 
        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
    }`}
  >
    <Icon className="h-5 w-5 mb-1" />
    <span className="text-[9px] font-medium leading-tight text-center">{label}</span>
    {badge > 0 && (
      <span className={`absolute -top-1 -right-1 ${
        badgeVariant === 'warning' ? 'bg-orange-500' : 
        badgeVariant === 'success' ? 'bg-green-500' : 'bg-red-500'
      } text-white text-[9px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center`}>
        {badge > 99 ? '99+' : badge}
      </span>
    )}
  </button>
);

const SuperAdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "overview");
  const [loading, setLoading] = useState(true);
  
  // Permission check helper
  const isSuperAdmin = user?.role === 'super_admin';
  
  // Define which tabs each role can access
  const tabPermissions = {
    // Accessible to both admin and super_admin
    admin: ['overview', 'transactions', 'support', 'ratings', 'alerts', 'documentation'],
    // Only super_admin
    super_admin: ['overview', 'users', 'transactions', 'analytics', 'facebook', 'support', 
                  'ratings', 'popups', 'sms', 'templates', 'faq', 'platforms', 
                  'testimonials', 'fraud-news', 'permissions', 'documentation', 'settings', 'alerts', 'finances', 'partners', 'promo-codes', 'audit']
  };
  
  // Check if current user can access a tab
  const canAccessTab = (tabName) => {
    const allowedTabs = isSuperAdmin ? tabPermissions.super_admin : tabPermissions.admin;
    return allowedTabs.includes(tabName);
  };
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [revenueChart, setRevenueChart] = useState(null);
  const [supportTickets, setSupportTickets] = useState([]);
  
  // Transaction filters
  const [statusFilter, setStatusFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [hiddenFilter, setHiddenFilter] = useState(false);
  
  // Settings states
  const [settings, setSettings] = useState({
    youtubeVideoUrl: "",
    videoSectionVisible: false,
    eventsSectionVisible: true,
    facebookGroupsSectionVisible: true,
    testimonialsSectionVisible: true,
    howItWorksSectionVisible: true,
    whyChooseSectionVisible: true,
    eventsPageVisible: true,
    facebookGroupsPageVisible: true,
    faqPageVisible: true,
    contactPageVisible: true,
    fraudCasesPageVisible: true
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  
  // Fee dialog states - Nouveau système simplifié
  const [showFeeDialog, setShowFeeDialog] = useState(false);
  const [feeType, setFeeType] = useState('fixed'); // 'fixed' ou 'percentage'
  const [feeValue, setFeeValue] = useState(24.95); // Valeur du frais ($ ou %)

  // Charger les frais depuis l'API
  const fetchTransferFee = async () => {
    try {
      const response = await fetch(`${API}/transfer-fee`);
      if (response.ok) {
        const data = await response.json();
        if (data.transfer_fee) {
          setFeeValue(data.transfer_fee);
          setFeeType(data.fee_type || 'fixed');
        }
      }
    } catch (error) {
      console.error('Error fetching transfer fee:', error);
    }
  };

  // Sauvegarder les frais
  const saveFeeSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          transfer_fee: feeType === 'fixed' ? feeValue : 0,
          fee_percentage: feeType === 'percentage' ? feeValue : 0,
          fee_type: feeType
        })
      });
      
      if (response.ok) {
        toast.success('Structure des frais mise à jour');
        setShowFeeDialog(false);
      } else {
        toast.error('Erreur lors de la mise à jour des frais');
      }
    } catch (error) {
      console.error('Error saving fee settings:', error);
      toast.error('Erreur de connexion');
    }
  };

  useEffect(() => {
    fetchAllData();
    fetchSettings();
    fetchTransferFee();
    
    // Real-time polling for notifications - every 30 seconds
    const pollInterval = setInterval(() => {
      refreshNotificationData();
    }, 30000);
    
    return () => clearInterval(pollInterval);
  }, []);

  // Lightweight refresh for notification counts only
  const refreshNotificationData = async () => {
    try {
      // Use admin endpoints (accessible to both admin and super_admin)
      const [usersRes, ticketsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/support/tickets`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/transactions`)
      ]);
      
      setUsers(usersRes.data?.users || usersRes.data || []);
      setSupportTickets(ticketsRes.data || []);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error("Failed to refresh notification data:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      if (response.data?.settings) {
        setSettings({
          youtubeVideoUrl: response.data.settings.youtubeVideoUrl || "",
          videoSectionVisible: response.data.settings.videoSectionVisible || false,
          eventsSectionVisible: response.data.settings.eventsSectionVisible !== false,
          facebookGroupsSectionVisible: response.data.settings.facebookGroupsSectionVisible !== false,
          testimonialsSectionVisible: response.data.settings.testimonialsSectionVisible !== false,
          howItWorksSectionVisible: response.data.settings.howItWorksSectionVisible !== false,
          whyChooseSectionVisible: response.data.settings.whyChooseSectionVisible !== false,
          eventsPageVisible: response.data.settings.eventsPageVisible !== false,
          facebookGroupsPageVisible: response.data.settings.facebookGroupsPageVisible !== false,
          faqPageVisible: response.data.settings.faqPageVisible !== false,
          contactPageVisible: response.data.settings.contactPageVisible !== false,
          fraudCasesPageVisible: response.data.settings.fraudCasesPageVisible !== false
        });
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  };

  const handleUpdateSettings = async (newSettings) => {
    setSettingsLoading(true);
    try {
      await axios.put(`${API}/admin/settings`, { settings: newSettings });
      setSettings(newSettings);
      toast.success("Paramètres mis à jour avec succès!");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour des paramètres");
    }
    setSettingsLoading(false);
  };

  const handleToggleVisibility = async (settingKey) => {
    const newSettings = {
      ...settings,
      [settingKey]: !settings[settingKey]
    };
    await handleUpdateSettings(newSettings);
  };

  const handleToggleVideoVisibility = async () => {
    await handleToggleVisibility('videoSectionVisible');
  };

  // Refetch transactions when filters change
  useEffect(() => {
    if (!loading) {
      fetchTransactions();
    }
  }, [statusFilter, searchFilter, hiddenFilter]);

  const fetchAllData = async () => {
    try {
      const [overviewRes, usersRes, revenueRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/business/overview`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/business/revenue-chart?days=30`),
        axios.get(`${API}/support/tickets`).catch(() => ({ data: [] }))
      ]);

      setOverview(overviewRes.data);
      setUsers(usersRes.data?.users || usersRes.data || []);
      setRevenueChart(revenueRes.data);
      setSupportTickets(ticketsRes.data || []);
      
      // Fetch transactions separately with filters
      await fetchTransactions();
    } catch (error) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  // Calculate notification counts
  const notificationCounts = useMemo(() => {
    // Pending support tickets (à traiter)
    const pendingSupportTickets = supportTickets.filter(
      t => t.status === 'a_traiter'
    ).length;
    
    // Pending transactions (awaiting action)
    const pendingTransactions = transactions.filter(
      t => ['pending', 'awaiting_payment', 'awaiting_confirmation'].includes(t.status)
    ).length;
    
    // New users in last 24 hours
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const newUsers = users.filter(u => {
      const createdAt = new Date(u.created_at);
      return createdAt > oneDayAgo;
    }).length;
    
    return {
      support: pendingSupportTickets,
      transactions: pendingTransactions,
      users: newUsers
    };
  }, [supportTickets, transactions, users]);

  const fetchTransactions = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchFilter) params.append('search', searchFilter);
      if (hiddenFilter) params.append('hidden', 'true');
      
      const response = await axios.get(`${API}/admin/transactions?${params}`);
      setTransactions(response.data);
    } catch (error) {
      toast.error("Failed to load transactions");
    }
  };

  const handleChangeTransactionStatus = async (transactionId, newStatus) => {
    try {
      await axios.put(`${API}/admin/transactions/${transactionId}`, { status: newStatus });
      toast.success("Transaction status updated!");
      fetchTransactions();
    } catch (error) {
      toast.error("Failed to update transaction");
    }
  };

  const handleToggleHideTransaction = async (transactionId, currentHidden) => {
    try {
      await axios.put(`${API}/admin/transactions/${transactionId}`, { hidden: !currentHidden });
      toast.success(currentHidden ? "Transaction unhidden" : "Transaction hidden");
      fetchTransactions();
    } catch (error) {
      toast.error("Failed to update transaction");
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm("Are you sure you want to permanently delete this transaction? This cannot be undone.")) return;
    
    try {
      await axios.delete(`${API}/admin/transactions/${transactionId}`);
      toast.success("Transaction deleted permanently");
      fetchTransactions();
    } catch (error) {
      toast.error("Failed to delete transaction");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: "bg-red-100 text-red-800",
      owner: "bg-brand-100 text-purple-800",
      admin: "bg-brand-100 text-blue-800",
      seller: "bg-green-100 text-green-800",
      buyer: "bg-gray-100 text-gray-800"
    };
    return colors[role] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du tableau de bord super admin...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 overflow-x-hidden">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-3 md:px-4 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center cursor-pointer hover:opacity-80 transition-opacity" 
              onClick={() => navigate('/')}
            >
              <img src="/logo-dark-cropped.png" alt="Middleman" className="h-5 md:h-6 w-auto" />
            </div>
            
            {/* Desktop Header Actions */}
            <div className="hidden md:flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="flex items-center space-x-2"
              >
                <Home className="h-4 w-4" />
                <span>Retour au site</span>
              </Button>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.name}</p>
                <Badge className="bg-red-600 text-white text-xs">{t('superAdmin.badge')}</Badge>
              </div>
              <LanguageSwitcher />
              <Button
                variant="outline"
                onClick={onLogout}
                data-testid="super-admin-logout"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t('common.logout')}
              </Button>
            </div>

            {/* Mobile Header Actions */}
            <div className="flex md:hidden items-center space-x-2">
              <Badge className="bg-red-600 text-white text-xs">{t('superAdmin.badge')}</Badge>
              <LanguageSwitcher />
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                data-testid="super-admin-logout-mobile"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-4 md:py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-1 md:pb-2 px-3 md:px-6 pt-3 md:pt-6">
              <CardTitle className="text-xs md:text-sm text-gray-600">{t('business.totalRevenue')}</CardTitle>
            </CardHeader>
            <CardContent className="px-3 md:px-6 pb-3 md:pb-6">
              <div className="text-xl md:text-3xl font-bold text-gray-900">
                {formatCurrency(overview?.total_revenue || 0)}
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-brand-500">
            <CardHeader className="pb-1 md:pb-2 px-3 md:px-6 pt-3 md:pt-6">
              <CardTitle className="text-xs md:text-sm text-gray-600">{t('business.platformFees')}</CardTitle>
            </CardHeader>
            <CardContent className="px-3 md:px-6 pb-3 md:pb-6">
              <div className="text-xl md:text-3xl font-bold text-gray-900">
                {formatCurrency(overview?.platform_fees_collected || 0)}
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-brand-500">
            <CardHeader className="pb-1 md:pb-2 px-3 md:px-6 pt-3 md:pt-6">
              <CardTitle className="text-xs md:text-sm text-gray-600">{t('admin.totalUsers')}</CardTitle>
            </CardHeader>
            <CardContent className="px-3 md:px-6 pb-3 md:pb-6">
              <div className="text-xl md:text-3xl font-bold text-gray-900">
                {overview?.total_users || 0}
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-1 md:pb-2 px-3 md:px-6 pt-3 md:pt-6">
              <CardTitle className="text-xs md:text-sm text-gray-600">{t('superAdmin.transactions')}</CardTitle>
            </CardHeader>
            <CardContent className="px-3 md:px-6 pb-3 md:pb-6">
              <div className="text-xl md:text-3xl font-bold text-gray-900">
                {overview?.total_transactions || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mobile Navigation - Grid of 5x2 Buttons */}
        <div className="md:hidden grid grid-cols-5 gap-1 mb-4">
          <MobileNavButton 
            icon={BarChart3} 
            label="Aperçu" 
            isActive={activeTab === 'overview'} 
            onClick={() => setActiveTab('overview')} 
          />
          <MobileNavButton 
            icon={Users} 
            label="Users" 
            isActive={activeTab === 'users'} 
            onClick={() => setActiveTab('users')}
            badge={notificationCounts.users}
            badgeVariant="success"
          />
          <MobileNavButton 
            icon={Package} 
            label="Trans." 
            isActive={activeTab === 'transactions'} 
            onClick={() => setActiveTab('transactions')}
            badge={notificationCounts.transactions}
            badgeVariant="warning"
          />
          <MobileNavButton 
            icon={BarChart3} 
            label="Stats" 
            isActive={activeTab === 'analytics'} 
            onClick={() => setActiveTab('analytics')} 
          />
          <MobileNavButton 
            icon={Facebook} 
            label="FB" 
            isActive={activeTab === 'facebook'} 
            onClick={() => setActiveTab('facebook')} 
          />
          <MobileNavButton 
            icon={Ticket} 
            label="Support" 
            isActive={activeTab === 'support'} 
            onClick={() => setActiveTab('support')}
            badge={notificationCounts.support}
            badgeVariant="default"
          />
          <MobileNavButton 
            icon={Star} 
            label="Éval." 
            isActive={activeTab === 'ratings'} 
            onClick={() => setActiveTab('ratings')} 
          />
          <MobileNavButton 
            icon={Bell} 
            label="Popups" 
            isActive={activeTab === 'popups'} 
            onClick={() => setActiveTab('popups')} 
          />
          <MobileNavButton 
            icon={MessageSquare} 
            label="SMS" 
            isActive={activeTab === 'sms'} 
            onClick={() => setActiveTab('sms')} 
          />
          <MobileNavButton 
            icon={FileText} 
            label="Templates" 
            isActive={activeTab === 'templates'} 
            onClick={() => setActiveTab('templates')} 
          />
          <MobileNavButton 
            icon={BookOpen} 
            label="FAQ" 
            isActive={activeTab === 'faq'} 
            onClick={() => setActiveTab('faq')} 
          />
          <MobileNavButton 
            icon={KeyRound} 
            label="Perms" 
            isActive={activeTab === 'permissions'} 
            onClick={() => setActiveTab('permissions')} 
          />
          <MobileNavButton 
            icon={HelpCircle} 
            label="Aide" 
            isActive={activeTab === 'documentation'} 
            onClick={() => setActiveTab('documentation')} 
          />
          <MobileNavButton 
            icon={Ticket} 
            label="Billets" 
            isActive={activeTab === 'platforms'} 
            onClick={() => setActiveTab('platforms')} 
          />
          <MobileNavButton 
            icon={MessageSquarePlus} 
            label="Témo." 
            isActive={activeTab === 'testimonials'} 
            onClick={() => setActiveTab('testimonials')} 
          />
          <MobileNavButton 
            icon={Newspaper} 
            label="Articles" 
            isActive={activeTab === 'fraud-news'} 
            onClick={() => setActiveTab('fraud-news')} 
          />
          <MobileNavButton 
            icon={Settings} 
            label="Config" 
            isActive={activeTab === 'settings'} 
            onClick={() => setActiveTab('settings')} 
          />
          <MobileNavButton 
            icon={Calculator} 
            label="Finances" 
            isActive={activeTab === 'finances'} 
            onClick={() => setActiveTab('finances')} 
          />
          <MobileNavButton 
            icon={Handshake} 
            label="Partenaires" 
            isActive={activeTab === 'partners'} 
            onClick={() => setActiveTab('partners')} 
          />
        </div>

        {/* Main Tabs - Desktop Only */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Desktop: Grid of button-style tabs */}
          <div className="hidden md:flex md:flex-wrap gap-2 mb-6 p-3 bg-gray-100/80 rounded-xl">
            {canAccessTab('overview') && (
              <button
                onClick={() => setActiveTab('overview')}
                data-testid="overview-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'overview' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <BarChart3 className="h-5 w-5" />
                <span className="text-xs font-medium">Aperçu</span>
              </button>
            )}
            {canAccessTab('users') && (
              <button
                onClick={() => setActiveTab('users')}
                data-testid="users-tab"
                className={`relative flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'users' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Users className="h-5 w-5" />
                <span className="text-xs font-medium">Utilisateurs</span>
                <NotificationBadge count={notificationCounts.users} variant="success" />
              </button>
            )}
            {canAccessTab('transactions') && (
              <button
                onClick={() => setActiveTab('transactions')}
                data-testid="transactions-tab"
                className={`relative flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'transactions' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Package className="h-5 w-5" />
                <span className="text-xs font-medium">Transactions</span>
                <NotificationBadge count={notificationCounts.transactions} variant="warning" />
              </button>
            )}
            {canAccessTab('analytics') && (
              <button
                onClick={() => setActiveTab('analytics')}
                data-testid="analytics-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'analytics' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <BarChart3 className="h-5 w-5" />
                <span className="text-xs font-medium">Statistiques</span>
              </button>
            )}
            {canAccessTab('facebook') && (
              <button
                onClick={() => setActiveTab('facebook')}
                data-testid="facebook-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'facebook' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Facebook className="h-5 w-5" />
                <span className="text-xs font-medium">Facebook</span>
              </button>
            )}
            {canAccessTab('support') && (
              <button
                onClick={() => setActiveTab('support')}
                data-testid="support-tab"
                className={`relative flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'support' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Ticket className="h-5 w-5" />
                <span className="text-xs font-medium">Support</span>
                <NotificationBadge count={notificationCounts.support} variant="default" />
              </button>
            )}
            {canAccessTab('ratings') && (
              <button
                onClick={() => setActiveTab('ratings')}
                data-testid="ratings-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'ratings' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Star className="h-5 w-5" />
                <span className="text-xs font-medium">Évaluations</span>
              </button>
            )}
            {canAccessTab('popups') && (
              <button
                onClick={() => setActiveTab('popups')}
                data-testid="popups-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'popups' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Bell className="h-5 w-5" />
                <span className="text-xs font-medium">Popups</span>
              </button>
            )}
            {canAccessTab('sms') && (
              <button
                onClick={() => setActiveTab('sms')}
                data-testid="sms-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'sms' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <MessageSquare className="h-5 w-5" />
                <span className="text-xs font-medium">SMS</span>
              </button>
            )}
            {canAccessTab('templates') && (
              <button
                onClick={() => setActiveTab('templates')}
                data-testid="templates-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'templates' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <FileText className="h-5 w-5" />
                <span className="text-xs font-medium">Templates</span>
              </button>
            )}
            {canAccessTab('faq') && (
              <button
                onClick={() => setActiveTab('faq')}
                data-testid="faq-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'faq' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <BookOpen className="h-5 w-5" />
                <span className="text-xs font-medium">FAQ</span>
              </button>
            )}
            {canAccessTab('permissions') && (
              <button
                onClick={() => setActiveTab('permissions')}
                data-testid="permissions-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'permissions' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <KeyRound className="h-5 w-5" />
                <span className="text-xs font-medium">Permissions</span>
              </button>
            )}
            {canAccessTab('documentation') && (
              <button
                onClick={() => setActiveTab('documentation')}
                data-testid="documentation-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'documentation' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <HelpCircle className="h-5 w-5" />
                <span className="text-xs font-medium">Aide</span>
              </button>
            )}
            {canAccessTab('platforms') && (
              <button
                onClick={() => setActiveTab('platforms')}
                data-testid="platforms-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'platforms' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Ticket className="h-5 w-5" />
                <span className="text-xs font-medium">Billets</span>
              </button>
            )}
            {canAccessTab('testimonials') && (
              <button
                onClick={() => setActiveTab('testimonials')}
                data-testid="testimonials-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'testimonials' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <MessageSquarePlus className="h-5 w-5" />
                <span className="text-xs font-medium">Témoignages</span>
              </button>
            )}
            {canAccessTab('fraud-news') && (
              <button
                onClick={() => setActiveTab('fraud-news')}
                data-testid="fraud-news-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'fraud-news' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Newspaper className="h-5 w-5" />
                <span className="text-xs font-medium">Articles fraudes</span>
              </button>
            )}
            {canAccessTab('partners') && (
              <button
                onClick={() => setActiveTab('partners')}
                data-testid="partners-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'partners' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Handshake className="h-5 w-5" />
                <span className="text-xs font-medium">Partenaires</span>
              </button>
            )}
            {canAccessTab('promo-codes') && (
              <button
                onClick={() => setActiveTab('promo-codes')}
                data-testid="promo-codes-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'promo-codes' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Gift className="h-5 w-5" />
                <span className="text-xs font-medium">Promos</span>
              </button>
            )}
            {canAccessTab('settings') && (
              <button
                onClick={() => setActiveTab('settings')}
                data-testid="settings-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'settings' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Settings className="h-5 w-5" />
                <span className="text-xs font-medium">Configuration</span>
              </button>
            )}
            {canAccessTab('alerts') && (
              <button
                onClick={() => setActiveTab('alerts')}
                data-testid="alerts-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'alerts' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <AlertTriangle className="h-5 w-5" />
                <span className="text-xs font-medium">Alertes</span>
              </button>
            )}
            {canAccessTab('finances') && (
              <button
                onClick={() => setActiveTab('finances')}
                data-testid="finances-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'finances' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Calculator className="h-5 w-5" />
                <span className="text-xs font-medium">Finances</span>
              </button>
            )}
            {canAccessTab('audit') && (
              <button
                onClick={() => setActiveTab('audit')}
                data-testid="audit-tab"
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[80px] ${
                  activeTab === 'audit' 
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Shield className="h-5 w-5" />
                <span className="text-xs font-medium">Audit</span>
              </button>
            )}
          </div>

          {/* Hidden TabsList for Radix functionality */}
          <TabsList className="hidden">
            <TabsTrigger value="overview">Aperçu</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="transactions">Trans.</TabsTrigger>
            <TabsTrigger value="analytics">Stats</TabsTrigger>
            <TabsTrigger value="facebook">FB</TabsTrigger>
            <TabsTrigger value="support">Support</TabsTrigger>
            <TabsTrigger value="ratings">Évaluations</TabsTrigger>
            <TabsTrigger value="popups">Popups</TabsTrigger>
            <TabsTrigger value="sms">SMS</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="faq">FAQ</TabsTrigger>
            <TabsTrigger value="platforms">Billets</TabsTrigger>
            <TabsTrigger value="testimonials">Témoignages</TabsTrigger>
            <TabsTrigger value="permissions">Perms</TabsTrigger>
            <TabsTrigger value="documentation">Aide</TabsTrigger>
            <TabsTrigger value="settings">Config</TabsTrigger>
            <TabsTrigger value="alerts">Alertes</TabsTrigger>
            <TabsTrigger value="finances">Finances</TabsTrigger>
            <TabsTrigger value="audit">Audit</TabsTrigger>
            <TabsTrigger value="partners">Partenaires</TabsTrigger>
            <TabsTrigger value="promo-codes">Promos</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Revenue Overview (Last 30 Days)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {revenueChart?.data?.slice(-10).map((day, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <div className="w-20 text-sm text-gray-600">
                          {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                        <div className="flex-1">
                          <div 
                            className="h-6 bg-gradient-to-r from-green-500 to-emerald-500 rounded"
                            style={{ width: `${(day.revenue / Math.max(...(revenueChart?.data || []).map(d => d.revenue))) * 100}%` }}
                          />
                        </div>
                        <div className="text-sm font-semibold">
                          {formatCurrency(day.revenue)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Platform Health</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Success Rate</span>
                      <span className="text-2xl font-bold text-green-600">
                        {100 - (overview?.dispute_rate || 0)}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Active Transactions</span>
                      <span className="text-2xl font-bold text-brand-600">
                        {overview?.active_transactions || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Completed</span>
                      <span className="text-2xl font-bold text-green-600">
                        {overview?.completed_transactions || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Disputes</span>
                      <span className="text-2xl font-bold text-red-600">
                        {overview?.disputed_transactions || 0}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>{t('superAdmin.userManagement')}</CardTitle>
                <CardDescription>{t('superAdmin.manageUsers')}</CardDescription>
              </CardHeader>
              <CardContent>
                <UserManagement currentUser={user} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <TransactionManagement token={localStorage.getItem('token')} />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <TransactionDashboard token={localStorage.getItem('token')} />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Configuration</CardTitle>
                <CardDescription>Paramètres de la plateforme et préférences</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Security Settings - 2FA */}
                  <div className="p-4 border rounded-lg bg-gradient-to-r from-brand-50 to-indigo-50">
                    <div className="flex items-center gap-2 mb-3">
                      <ShieldCheck className="h-5 w-5 text-brand-600" />
                      <h3 className="font-semibold">Sécurité du compte</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Protégez votre compte administrateur avec l'authentification à deux facteurs (2FA)
                    </p>
                    <TwoFactorSetup user={user} onStatusChange={(enabled) => {
                      toast.success(enabled ? "2FA activé" : "2FA désactivé");
                    }} />
                  </div>

                  {/* Video Section Settings */}
                  <div className="p-4 border rounded-lg bg-gradient-to-r from-brand-50 to-cyan-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold mb-2 flex items-center">
                          <Eye className="h-5 w-5 mr-2 text-brand-600" />
                          Section Vidéo (Page d'accueil)
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                          Afficher ou masquer la section vidéo YouTube sur la page d'accueil
                        </p>
                        {settings.youtubeVideoUrl && (
                          <p className="text-xs text-gray-500">
                            URL actuelle: {settings.youtubeVideoUrl}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`text-sm font-medium ${settings.videoSectionVisible ? 'text-green-600' : 'text-gray-500'}`}>
                          {settings.videoSectionVisible ? 'Visible' : 'Masquée'}
                        </span>
                        <button
                          onClick={handleToggleVideoVisibility}
                          disabled={settingsLoading}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                            settings.videoSectionVisible ? 'bg-brand-600' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          data-testid="video-visibility-toggle"
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              settings.videoSectionVisible ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Section Visibility Controls */}
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-semibold mb-4 flex items-center">
                      <Eye className="h-5 w-5 mr-2 text-brand-600" />
                      Visibilité des sections (Page d'accueil)
                    </h3>
                    <div className="space-y-3">
                      {[
                        { key: 'eventsSectionVisible', label: 'Section Événements' },
                        { key: 'facebookGroupsSectionVisible', label: 'Section Groupes Facebook' },
                        { key: 'testimonialsSectionVisible', label: 'Section Témoignages' },
                        { key: 'howItWorksSectionVisible', label: 'Section Comment ça fonctionne' },
                        { key: 'whyChooseSectionVisible', label: 'Section Pourquoi nous choisir' },
                      ].map((item) => (
                        <div key={item.key} className="flex items-center justify-between py-2 border-b last:border-0">
                          <span className="text-sm">{item.label}</span>
                          <div className="flex items-center space-x-3">
                            <span className={`text-xs font-medium ${settings[item.key] ? 'text-green-600' : 'text-gray-500'}`}>
                              {settings[item.key] ? 'Visible' : 'Masquée'}
                            </span>
                            <button
                              onClick={() => handleToggleVisibility(item.key)}
                              disabled={settingsLoading}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                settings[item.key] ? 'bg-green-500' : 'bg-gray-300'
                              } ${settingsLoading ? 'opacity-50' : ''}`}
                            >
                              <span
                                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                  settings[item.key] ? 'translate-x-5' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Page Visibility Controls */}
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-semibold mb-4 flex items-center">
                      <Eye className="h-5 w-5 mr-2 text-brand-600" />
                      Visibilité des pages
                    </h3>
                    <div className="space-y-3">
                      {[
                        { key: 'eventsPageVisible', label: 'Page Événements' },
                        { key: 'facebookGroupsPageVisible', label: 'Page Groupes Facebook' },
                        { key: 'faqPageVisible', label: 'Page FAQ' },
                        { key: 'contactPageVisible', label: 'Page Contact' },
                        { key: 'fraudCasesPageVisible', label: 'Page Cas de fraudes' },
                      ].map((item) => (
                        <div key={item.key} className="flex items-center justify-between py-2 border-b last:border-0">
                          <span className="text-sm">{item.label}</span>
                          <div className="flex items-center space-x-3">
                            <span className={`text-xs font-medium ${settings[item.key] ? 'text-green-600' : 'text-gray-500'}`}>
                              {settings[item.key] ? 'Active' : 'Désactivée'}
                            </span>
                            <button
                              onClick={() => handleToggleVisibility(item.key)}
                              disabled={settingsLoading}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                settings[item.key] ? 'bg-green-500' : 'bg-gray-300'
                              } ${settingsLoading ? 'opacity-50' : ''}`}
                            >
                              <span
                                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                  settings[item.key] ? 'translate-x-5' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h3 className="font-semibold mb-2">Frais de transfert</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Frais actuel: {feeType === 'fixed' 
                        ? `${feeValue.toFixed(2)}$ (fixe)` 
                        : `${feeValue}% du montant`}
                    </p>
                    <Button variant="outline" onClick={() => setShowFeeDialog(true)}>Modifier les frais</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Fee Structure Dialog - Système simplifié */}
            <Dialog open={showFeeDialog} onOpenChange={setShowFeeDialog}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Modifier les frais de transfert</DialogTitle>
                  <DialogDescription>
                    Configurez le type et le montant des frais appliqués aux transferts
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {/* Type de frais */}
                  <div>
                    <Label className="mb-2 block">Type de frais</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setFeeType('fixed')}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          feeType === 'fixed' 
                            ? 'border-brand-600 bg-brand-50 text-brand-700' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <DollarSign className="h-5 w-5 mx-auto mb-1" />
                        <span className="text-sm font-medium">Frais fixe</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFeeType('percentage')}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          feeType === 'percentage' 
                            ? 'border-brand-600 bg-brand-50 text-brand-700' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-lg font-bold mb-1 block">%</span>
                        <span className="text-sm font-medium">Pourcentage</span>
                      </button>
                    </div>
                  </div>

                  {/* Valeur du frais */}
                  <div>
                    <Label>
                      {feeType === 'fixed' ? 'Montant fixe ($)' : 'Pourcentage (%)'}
                    </Label>
                    <Input 
                      type="number" 
                      min="0" 
                      max={feeType === 'percentage' ? 100 : undefined}
                      step={feeType === 'fixed' ? '0.01' : '0.5'}
                      value={feeValue}
                      onChange={(e) => setFeeValue(parseFloat(e.target.value) || 0)}
                      className="text-lg font-semibold"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {feeType === 'fixed' 
                        ? 'Ce montant sera facturé pour chaque transfert' 
                        : 'Ce pourcentage sera appliqué au montant du transfert'}
                    </p>
                  </div>
                  
                  {/* Aperçu */}
                  <div className="bg-brand-50 border border-brand-200 p-4 rounded-lg">
                    <h4 className="font-medium text-sm mb-2 text-brand-800">Aperçu du frais</h4>
                    {feeType === 'fixed' ? (
                      <p className="text-brand-700">
                        Chaque transfert coûtera <span className="font-bold text-lg">{feeValue.toFixed(2)}$</span>
                      </p>
                    ) : (
                      <div className="text-brand-700 space-y-1">
                        <p>Pour un transfert de <span className="font-bold">100$</span>: <span className="font-bold">{(100 * feeValue / 100).toFixed(2)}$</span></p>
                        <p>Pour un transfert de <span className="font-bold">250$</span>: <span className="font-bold">{(250 * feeValue / 100).toFixed(2)}$</span></p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowFeeDialog(false)}>Annuler</Button>
                  <Button 
                    className="bg-brand-600 hover:bg-brand-700"
                    onClick={saveFeeSettings}
                  >
                    Sauvegarder
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Facebook Groups Tab */}
          <TabsContent value="facebook">
            <FacebookGroupsAdmin />
          </TabsContent>

          {/* Support Tickets Tab */}
          <TabsContent value="support">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="h-5 w-5" />
                  Gestion des tickets de support
                </CardTitle>
                <CardDescription>
                  Gérez les demandes de support des clients, assignez les tickets et suivez les délais de réponse
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SupportTicketManager currentUser={user} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documentation Tab */}
          <TabsContent value="documentation">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-brand-600" />
                  Documentation de la plateforme
                </CardTitle>
                <CardDescription>
                  Guide complet des fonctionnalités pour les utilisateurs et les administrateurs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PlatformDocumentation />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Permissions Tab */}
          <TabsContent value="permissions">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <KeyRound className="h-5 w-5 text-brand-600" />
                  Gestion des permissions administrateurs
                </CardTitle>
                <CardDescription>
                  Définissez les fonctionnalités accessibles pour chaque administrateur
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AdminPermissionsManager />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ratings Tab */}
          <TabsContent value="ratings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-amber-500" />
                  Évaluations utilisateurs
                </CardTitle>
                <CardDescription>
                  Consultez les avis et notes laissés par les utilisateurs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RatingsManager />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Popups Tab */}
          <TabsContent value="popups">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-brand-600" />
                  Gestion des popups
                </CardTitle>
                <CardDescription>
                  Créez et gérez les notifications popup pour vos utilisateurs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PopupManager />
              </CardContent>
            </Card>
          </TabsContent>

          {/* SMS Notifications Tab */}
          <TabsContent value="sms">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-brand-600" />
                  Notifications SMS
                </CardTitle>
                <CardDescription>
                  Configurez quelles notifications SMS sont envoyées et à qui
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SMSNotificationManager />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notification Templates Tab */}
          <TabsContent value="templates">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-brand-600" />
                  Modèles de notifications
                </CardTitle>
                <CardDescription>
                  Personnalisez le contenu des messages SMS et emails envoyés par la plateforme
                </CardDescription>
              </CardHeader>
              <CardContent>
                <NotificationTemplateManager />
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQ Management Tab */}
          <TabsContent value="faq">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-brand-600" />
                  Gestion des FAQ
                </CardTitle>
                <CardDescription>
                  Créez et gérez les questions fréquentes affichées sur le site
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FAQManager />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Platform Management Tab */}
          <TabsContent value="platforms">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="h-5 w-5 text-[#2eb382]" />
                  Gestion des plateformes de billets
                </CardTitle>
                <CardDescription>
                  Ajoutez, modifiez ou masquez les plateformes de billets dans le formulaire de transfert
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PlatformManagement />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="testimonials">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquarePlus className="h-5 w-5 text-[#2eb382]" />
                  Gestion des témoignages
                </CardTitle>
                <CardDescription>
                  Approuvez, mettez en avant ou masquez les témoignages des utilisateurs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TestimonialManagement token={localStorage.getItem('token')} />
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Fraud News Tab */}
          <TabsContent value="fraud-news">
            <FraudNewsAdmin token={localStorage.getItem('token')} />
          </TabsContent>
          
          {/* Transfer Alerts Tab */}
          <TabsContent value="alerts">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  Alertes de Transferts
                </CardTitle>
                <CardDescription>
                  Surveillance des transferts dépassant les délais normaux de traitement
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TransferAlerts token={localStorage.getItem('token')} />
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Financial Dashboard Tab */}
          <TabsContent value="finances">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-emerald-600" />
                  Tableau de bord financier
                </CardTitle>
                <CardDescription>
                  Suivi des ventes, dépenses, budgets et taxes de l'entreprise
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FinancialDashboard token={localStorage.getItem('token')} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <PartnerManagement />
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promo-codes">
            <PromoCodeManagement />
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <AuditLogViewer />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;